// A localStorage-backed boolean opt-in that fails closed. The value is the
// string "1" when opted in and the key is removed otherwise, so a foreign value
// (or storage that throws) reads as opted-out. An in-memory override records the
// outcome of a failed write so a subsequent read stays consistent within the
// session even when storage is unavailable.
//
// Insights passes `onChange` to fan a same-document event out to its
// `useSyncExternalStore` subscribers. Calendar has its own preference because
// it defaults to new and must distinguish explicit opt-outs from no choice.

type OptInStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type OptInStorageProvider = () => OptInStorage | undefined;

export interface StoredOptInPreference {
  isOptedIn(): boolean;
  optIn(): boolean;
  optOut(): boolean;
}

const getBrowserStorage: OptInStorageProvider = () => window.localStorage;

export function createStoredOptInPreference(
  key: string,
  { onChange }: { onChange?: () => void } = {},
  getStorage: OptInStorageProvider = getBrowserStorage
): StoredOptInPreference {
  let inMemoryOptInOverride: boolean | undefined;

  const resolveStorage = () => {
    try {
      return getStorage();
    } catch {
      return undefined;
    }
  };

  const emit = () => onChange?.();

  return {
    isOptedIn(): boolean {
      if (inMemoryOptInOverride !== undefined) {
        return inMemoryOptInOverride;
      }

      try {
        return resolveStorage()?.getItem(key) === "1";
      } catch {
        return false;
      }
    },

    optIn(): boolean {
      try {
        const storage = resolveStorage();
        if (!storage) {
          inMemoryOptInOverride = false;
          return false;
        }
        storage.setItem(key, "1");
        inMemoryOptInOverride = undefined;
        emit();
        return true;
      } catch {
        inMemoryOptInOverride = false;
        return false;
      }
    },

    optOut(): boolean {
      // Opt-out is the safe direction, so pin the in-memory answer to false up
      // front and clear it only once storage confirms the removal. A single
      // emit at the end covers every path — subscribers re-read the same false
      // whether storage succeeded, was absent, or threw.
      inMemoryOptInOverride = false;
      let removed = false;
      try {
        const storage = resolveStorage();
        if (storage) {
          storage.removeItem(key);
          inMemoryOptInOverride = undefined;
          removed = true;
        }
      } catch {
        // Keep the override at false; the session stays opted-out.
      }
      emit();
      return removed;
    },
  };
}
